import React, { useMemo } from "react";

type Props = {
  data: number[];
  width?: number;
  height?: number;
  showArea?: boolean;
  showGrid?: boolean;
  showPoints?: boolean;
  showValues?: boolean;
  color?: string;
};

/**
 * Lightweight SVG LineChart (no external chart libs).
 */
export default function LineChart({
  data,
  width = 800,
  height = 224,
  showArea = false,
  showGrid = true,
  showPoints = true,
  showValues = false,
  color = "#3b82f6",
}: Props) {
  const padding = 24;
  const innerW = width - padding * 2;
  const innerH = height - padding * 2;
  const max = Math.max(1, ...data.map((n) => (Number.isFinite(n) ? n : 0)));

  const points = useMemo(() => {
    if (!data.length) return [];
    return data.map((v, i) => {
      const value = Number.isFinite(v) ? v : 0;
      const x = padding + (innerW * (i / Math.max(1, data.length - 1)));
      const y = padding + (innerH - (value / max) * innerH);
      return { x, y, value };
    });
  }, [data, innerH, innerW, max, padding]);

  const d = points.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x} ${p.y}`).join(" ");
  const areaD = `${d} L ${padding + innerW} ${padding + innerH} L ${padding} ${padding + innerH} Z`;

  const gridLines = showGrid ? 4 : 0;
  const grid = Array.from({ length: gridLines + 1 }).map((_, idx) => {
    const y = padding + (innerH / gridLines) * idx;
    return y;
  });

  return (
    <svg viewBox={`0 0 ${width} ${height}`} width="100%" height="100%" role="img" aria-label="Line chart">
      <rect x="0" y="0" width={width} height={height} fill="transparent" />
      {showGrid &&
        grid.map((y, i) => (
          <line
            key={i}
            x1={padding}
            x2={width - padding}
            y1={y}
            y2={y}
            stroke="rgba(148,163,184,0.35)"
            strokeWidth="1"
          />
        ))}

      {showArea ? <path d={areaD} fill={color} opacity="0.12" /> : null}
      <path d={d} fill="none" stroke={color} strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />

      {showPoints &&
        points.map((p, i) => (
          <g key={i}>
            <circle cx={p.x} cy={p.y} r="3.5" fill={color} />
            {showValues ? (
              <text x={p.x} y={p.y - 8} textAnchor="middle" fontSize="10" fill="currentColor" opacity="0.85">
                {p.value}
              </text>
            ) : null}
          </g>
        ))}
    </svg>
  );
}


